import {getParkingPlacesType} from "../../../offer/detail/constants/ParkingPlacesType";
import {getOfferTypeName} from "../../../offer/helpers/OfferType";
import {OfferType} from "../../../offer/types/OfferType";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IRecommendedProperty} from "../../../property/types/IRecommendedProperty";
import {IUserProfile} from "../../../user/types/IUserProfile";
import {getThumbnailLink} from "../../../utils/get_thumbnail_link";
import {IValueLabel} from "../../../variables/types/IValueLabel";
import {IApplicationData} from "../../types/IApplicationData";
import {ISentLeadStoredValues} from "../../types/lead/ISentLeadStoredValues";
import {ITinderMultiLeadSentItem} from "../../types/tinder_multi_lead/ITinderMultiLeadState";
import {TinderMultiLeadProperty} from "../../types/tinder_multi_lead/TinderMultiLeadProperty";
import {getSentLeadFormValuesFromStorage} from "../../utils/lead/sent_lead_form_values";

export const TINDER_MULTI_LEAD_EMPTY_VALUE = "informacja u dewelopera";

type TinderMultiLeadOfferRegion = Partial<IRecommendedProperty["offer"]["region"]>;

interface ITinderMultiLeadVariables {
    offer_facilities?: IValueLabel[];
    offer_standard_description_type?: IValueLabel[];
    property_additional_areas?: IValueLabel[];
    property_kitchen_type?: IValueLabel[];
    property_quarters?: IValueLabel[];
}

export interface ITinderMultiLeadInfoItem {
    label: string;
    value: string;
}

function getTinderMultiLeadOfferRegion(property: TinderMultiLeadProperty): TinderMultiLeadOfferRegion | undefined {
    return (property.offer as TinderMultiLeadProperty["offer"] & {region?: TinderMultiLeadOfferRegion}).region;
}

export function getTinderMultiLeadOfferTypeName(offerType: number): string {
    try {
        return getOfferTypeName(offerType as OfferType);
    } catch (_error) {
        return "nieruchomość";
    }
}

export function getTinderMultiLeadPropertyTypeDescription(rooms: number, offerType: number): string {
    const roomAdjective =
        offerType === OfferType.FLAT ? "pokojowe" : offerType === OfferType.HOUSE || offerType === OfferType.COMMERCIAL ? "pokojowy" : "pokojowa";

    return `${rooms}-${roomAdjective} ${getTinderMultiLeadOfferTypeName(offerType)}`;
}

export function getTinderMultiLeadLocationName(property: TinderMultiLeadProperty): string {
    const offerRegion = getTinderMultiLeadOfferRegion(property);

    return offerRegion?.name_declension_where || offerRegion?.full_name || property.offer.address || TINDER_MULTI_LEAD_EMPTY_VALUE;
}

export function getTinderMultiLeadAddress(property: TinderMultiLeadProperty): string {
    const offerRegion = getTinderMultiLeadOfferRegion(property);

    return property.offer.address || offerRegion?.full_name || offerRegion?.name_declension_where || TINDER_MULTI_LEAD_EMPTY_VALUE;
}

export interface ITinderMultiLeadContactValues {
    email: string;
    marketing_email: boolean;
    marketing_sms: boolean;
    name: string;
    phone: string;
}

interface ITinderMultiLeadContactValueSources {
    originalApplication?: IApplicationData | null;
    userProfile?: IUserProfile | null;
}

function hasCompleteContactValues<T extends {email?: string | null; name?: string | null; phone?: string | null}>(
    values: T | null | undefined
): values is T & {email: string; name: string; phone: string} {
    return Boolean(values?.name && values.email && values.phone);
}

function getTinderMultiLeadContactValuesFromApplication(originalApplication: IApplicationData | null | undefined): ITinderMultiLeadContactValues | null {
    if (!hasCompleteContactValues(originalApplication)) {
        return null;
    }

    return {
        email: originalApplication.email,
        marketing_email: typeof originalApplication.marketing_email === "boolean" ? originalApplication.marketing_email : true,
        marketing_sms: typeof originalApplication.marketing_sms === "boolean" ? originalApplication.marketing_sms : false,
        name: originalApplication.name,
        phone: originalApplication.phone
    };
}

function getTinderMultiLeadContactValuesFromStorage(values: ISentLeadStoredValues | null): ITinderMultiLeadContactValues | null {
    if (!hasCompleteContactValues(values)) {
        return null;
    }

    return {
        email: values.email,
        marketing_email: values.marketing_email,
        marketing_sms: values.marketing_sms,
        name: values.name,
        phone: values.phone
    };
}

function getTinderMultiLeadContactValuesFromProfile(userProfile: IUserProfile | null | undefined): ITinderMultiLeadContactValues | null {
    if (!hasCompleteContactValues(userProfile)) {
        return null;
    }

    return {
        email: userProfile.email,
        marketing_email: typeof userProfile.email_marketing_consent === "boolean" ? userProfile.email_marketing_consent : true,
        marketing_sms: typeof userProfile.phone_marketing_consent === "boolean" ? userProfile.phone_marketing_consent : false,
        name: userProfile.name,
        phone: userProfile.phone
    };
}

export function getTinderMultiLeadContactValues({
    originalApplication,
    userProfile
}: ITinderMultiLeadContactValueSources = {}): ITinderMultiLeadContactValues | null {
    const originalApplicationContactValues = getTinderMultiLeadContactValuesFromApplication(originalApplication);

    if (originalApplicationContactValues) {
        return originalApplicationContactValues;
    }

    const values = getSentLeadFormValuesFromStorage();
    const storageContactValues = getTinderMultiLeadContactValuesFromStorage(values);

    if (storageContactValues) {
        return storageContactValues;
    }

    return getTinderMultiLeadContactValuesFromProfile(userProfile);
}

export function getTinderMultiLeadImage(property: IPropertyDetail | IRecommendedProperty | null): string | null {
    if (!property?.offer.main_image) {
        return null;
    }

    return (
        getThumbnailLink(property.offer.main_image_url, 760, 428) ||
        ("m_img_983x552" in property.offer.main_image && property.offer.main_image.m_img_983x552) ||
        ("m_img_760x428" in property.offer.main_image && property.offer.main_image.m_img_760x428) ||
        ("m_img_350x230" in property.offer.main_image && property.offer.main_image.m_img_350x230) ||
        property.offer.main_image.m_img_224x147 ||
        property.offer.main_image.m_img_155x87 ||
        null
    );
}

export function getTinderMultiLeadSentItem(application: IApplicationData, property: IPropertyDetail | IRecommendedProperty): ITinderMultiLeadSentItem {
    return {
        address: getTinderMultiLeadAddress(property),
        applicationUuid: application.uuid,
        area: property.area,
        image: getTinderMultiLeadImage(property),
        offerId: property.offer.id,
        offerName: property.offer.name,
        offerType: property.offer.type as OfferType,
        price: property.price,
        propertyId: property.id,
        rooms: property.rooms,
        vendorName: property.offer.vendor.name
    };
}

export function getTinderMultiLeadAdditionalAreas(property: IPropertyDetail, variables: ITinderMultiLeadVariables | null): string {
    const labels = property.additional_areas.map(({type}) => variables?.property_additional_areas?.find((item) => item.value === type)?.label).filter(Boolean);

    return labels.length ? labels.join(", ") : TINDER_MULTI_LEAD_EMPTY_VALUE;
}

export function getTinderMultiLeadQuarters(property: IPropertyDetail, variables: ITinderMultiLeadVariables | null): string {
    const labels = property.quarters
        .map((quarter) => variables?.property_quarters?.find((item) => item.value === quarter)?.label?.toLowerCase())
        .filter(Boolean);

    return labels.length ? labels.join(", ") : TINDER_MULTI_LEAD_EMPTY_VALUE;
}

export function getTinderMultiLeadKitchen(property: IPropertyDetail, variables: ITinderMultiLeadVariables | null): string {
    return variables?.property_kitchen_type?.find((item) => item.value === property.kitchen_type)?.label || TINDER_MULTI_LEAD_EMPTY_VALUE;
}

export function getTinderMultiLeadInvestmentRows(property: IPropertyDetail, variables: ITinderMultiLeadVariables | null): ITinderMultiLeadInfoItem[] {
    const parkingPlaces = property.offer.parking_places
        .map((parkingPlace) => {
            try {
                return getParkingPlacesType(parkingPlace.type).toLowerCase();
            } catch (_error) {
                return null;
            }
        })
        .filter(Boolean);

    const standardDescriptionType = property.offer.standard_description?.types?.[0];
    const standard = standardDescriptionType ? variables?.offer_standard_description_type?.find((item) => item.value === standardDescriptionType)?.label : null;

    const height =
        property.height_range?.lower && property.height_range?.upper
            ? property.height_range.lower === property.height_range.upper
                ? `${property.height_range.lower} cm`
                : `${property.height_range.lower}-${property.height_range.upper} cm`
            : null;

    const facilities = (property.offer.facilities ?? [])
        .map((facility) => variables?.offer_facilities?.find((item) => item.value === facility)?.label)
        .filter(Boolean);

    const uniqueParkingPlaces = Array.from(new Set(parkingPlaces));

    return [
        {label: "Miejsca postojowe", value: uniqueParkingPlaces.length ? uniqueParkingPlaces.join(", ") : TINDER_MULTI_LEAD_EMPTY_VALUE},
        {
            label: "Komórki lokatorskie",
            value: property.offer.storage?.quantity ? `${property.offer.storage.quantity} dostępne` : TINDER_MULTI_LEAD_EMPTY_VALUE
        },
        {label: "Standard wykończenia", value: standard || TINDER_MULTI_LEAD_EMPTY_VALUE},
        {label: "Rodzaj ogrzewania", value: property.offer.standard_description?.heating || TINDER_MULTI_LEAD_EMPTY_VALUE},
        {label: "Wysokość pomieszczeń", value: height || TINDER_MULTI_LEAD_EMPTY_VALUE},
        {label: "Udogodnienia", value: facilities.length ? facilities.join(", ") : TINDER_MULTI_LEAD_EMPTY_VALUE}
    ];
}
import {getParkingPlacesType} from "../../../offer/detail/constants/ParkingPlacesType";
import {getOfferTypeName} from "../../../offer/helpers/OfferType";
import {OfferType} from "../../../offer/types/OfferType";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IRecommendedProperty} from "../../../property/types/IRecommendedProperty";
import {IUserProfile} from "../../../user/types/IUserProfile";
import {getThumbnailLink} from "../../../utils/get_thumbnail_link";
import {IValueLabel} from "../../../variables/types/IValueLabel";
import {IApplicationData} from "../../types/IApplicationData";
import {ISentLeadStoredValues} from "../../types/lead/ISentLeadStoredValues";
import {ITinderMultiLeadSentItem} from "../../types/tinder_multi_lead/ITinderMultiLeadState";
import {TinderMultiLeadProperty} from "../../types/tinder_multi_lead/TinderMultiLeadProperty";
import {getSentLeadFormValuesFromStorage} from "../../utils/lead/sent_lead_form_values";

export const TINDER_MULTI_LEAD_EMPTY_VALUE = "informacja u dewelopera";

type TinderMultiLeadOfferRegion = Partial<IRecommendedProperty["offer"]["region"]>;

interface ITinderMultiLeadVariables {
    offer_facilities?: IValueLabel[];
    offer_standard_description_type?: IValueLabel[];
    property_additional_areas?: IValueLabel[];
    property_kitchen_type?: IValueLabel[];
    property_quarters?: IValueLabel[];
}

export interface ITinderMultiLeadInfoItem {
    label: string;
    value: string;
}

function getTinderMultiLeadOfferRegion(property: TinderMultiLeadProperty): TinderMultiLeadOfferRegion | undefined {
    return (property.offer as TinderMultiLeadProperty["offer"] & {region?: TinderMultiLeadOfferRegion}).region;
}

export function getTinderMultiLeadOfferTypeName(offerType: number): string {
    try {
        return getOfferTypeName(offerType as OfferType);
    } catch (_error) {
        return "nieruchomość";
    }
}

export function getTinderMultiLeadPropertyTypeDescription(rooms: number, offerType: number): string {
    const roomAdjective =
        offerType === OfferType.FLAT ? "pokojowe" : offerType === OfferType.HOUSE || offerType === OfferType.COMMERCIAL ? "pokojowy" : "pokojowa";

    return `${rooms}-${roomAdjective} ${getTinderMultiLeadOfferTypeName(offerType)}`;
}

export function getTinderMultiLeadLocationName(property: TinderMultiLeadProperty): string {
    const offerRegion = getTinderMultiLeadOfferRegion(property);

    return offerRegion?.name_declension_where || offerRegion?.full_name || property.offer.address || TINDER_MULTI_LEAD_EMPTY_VALUE;
}

export function getTinderMultiLeadAddress(property: TinderMultiLeadProperty): string {
    const offerRegion = getTinderMultiLeadOfferRegion(property);

    return property.offer.address || offerRegion?.full_name || offerRegion?.name_declension_where || TINDER_MULTI_LEAD_EMPTY_VALUE;
}

export interface ITinderMultiLeadContactValues {
    email: string;
    marketing_email: boolean;
    marketing_sms: boolean;
    name: string;
    phone: string;
}

interface ITinderMultiLeadContactValueSources {
    originalApplication?: IApplicationData | null;
    userProfile?: IUserProfile | null;
}

function hasCompleteContactValues<T extends {email?: string | null; name?: string | null; phone?: string | null}>(
    values: T | null | undefined
): values is T & {email: string; name: string; phone: string} {
    return Boolean(values?.name && values.email && values.phone);
}

function getTinderMultiLeadContactValuesFromApplication(originalApplication: IApplicationData | null | undefined): ITinderMultiLeadContactValues | null {
    if (!hasCompleteContactValues(originalApplication)) {
        return null;
    }

    return {
        email: originalApplication.email,
        marketing_email: typeof originalApplication.marketing_email === "boolean" ? originalApplication.marketing_email : true,
        marketing_sms: typeof originalApplication.marketing_sms === "boolean" ? originalApplication.marketing_sms : false,
        name: originalApplication.name,
        phone: originalApplication.phone
    };
}

function getTinderMultiLeadContactValuesFromStorage(values: ISentLeadStoredValues | null): ITinderMultiLeadContactValues | null {
    if (!hasCompleteContactValues(values)) {
        return null;
    }

    return {
        email: values.email,
        marketing_email: values.marketing_email,
        marketing_sms: values.marketing_sms,
        name: values.name,
        phone: values.phone
    };
}

function getTinderMultiLeadContactValuesFromProfile(userProfile: IUserProfile | null | undefined): ITinderMultiLeadContactValues | null {
    if (!hasCompleteContactValues(userProfile)) {
        return null;
    }

    return {
        email: userProfile.email,
        marketing_email: typeof userProfile.email_marketing_consent === "boolean" ? userProfile.email_marketing_consent : true,
        marketing_sms: typeof userProfile.phone_marketing_consent === "boolean" ? userProfile.phone_marketing_consent : false,
        name: userProfile.name,
        phone: userProfile.phone
    };
}

export function getTinderMultiLeadContactValues({
    originalApplication,
    userProfile
}: ITinderMultiLeadContactValueSources = {}): ITinderMultiLeadContactValues | null {
    const originalApplicationContactValues = getTinderMultiLeadContactValuesFromApplication(originalApplication);

    if (originalApplicationContactValues) {
        return originalApplicationContactValues;
    }

    const values = getSentLeadFormValuesFromStorage();
    const storageContactValues = getTinderMultiLeadContactValuesFromStorage(values);

    if (storageContactValues) {
        return storageContactValues;
    }

    return getTinderMultiLeadContactValuesFromProfile(userProfile);
}

export function getTinderMultiLeadImage(property: IPropertyDetail | IRecommendedProperty | null): string | null {
    if (!property?.offer.main_image) {
        return null;
    }

    return (
        getThumbnailLink(property.offer.main_image_url, 760, 428) ||
        ("m_img_983x552" in property.offer.main_image && property.offer.main_image.m_img_983x552) ||
        ("m_img_760x428" in property.offer.main_image && property.offer.main_image.m_img_760x428) ||
        ("m_img_350x230" in property.offer.main_image && property.offer.main_image.m_img_350x230) ||
        property.offer.main_image.m_img_224x147 ||
        property.offer.main_image.m_img_155x87 ||
        null
    );
}

export function getTinderMultiLeadSentItem(application: IApplicationData, property: IPropertyDetail | IRecommendedProperty): ITinderMultiLeadSentItem {
    return {
        address: getTinderMultiLeadAddress(property),
        applicationUuid: application.uuid,
        area: property.area,
        image: getTinderMultiLeadImage(property),
        offerId: property.offer.id,
        offerName: property.offer.name,
        offerType: property.offer.type as OfferType,
        price: property.price,
        propertyId: property.id,
        rooms: property.rooms,
        vendorName: property.offer.vendor.name
    };
}

export function getTinderMultiLeadAdditionalAreas(property: IPropertyDetail, variables: ITinderMultiLeadVariables | null): string {
    const labels = property.additional_areas.map(({type}) => variables?.property_additional_areas?.find((item) => item.value === type)?.label).filter(Boolean);

    return labels.length ? labels.join(", ") : TINDER_MULTI_LEAD_EMPTY_VALUE;
}

export function getTinderMultiLeadQuarters(property: IPropertyDetail, variables: ITinderMultiLeadVariables | null): string {
    const labels = property.quarters
        .map((quarter) => variables?.property_quarters?.find((item) => item.value === quarter)?.label?.toLowerCase())
        .filter(Boolean);

    return labels.length ? labels.join(", ") : TINDER_MULTI_LEAD_EMPTY_VALUE;
}

export function getTinderMultiLeadKitchen(property: IPropertyDetail, variables: ITinderMultiLeadVariables | null): string {
    return variables?.property_kitchen_type?.find((item) => item.value === property.kitchen_type)?.label || TINDER_MULTI_LEAD_EMPTY_VALUE;
}

export function getTinderMultiLeadInvestmentRows(property: IPropertyDetail, variables: ITinderMultiLeadVariables | null): ITinderMultiLeadInfoItem[] {
    const parkingPlaces = property.offer.parking_places
        .map((parkingPlace) => {
            try {
                return getParkingPlacesType(parkingPlace.type).toLowerCase();
            } catch (_error) {
                return null;
            }
        })
        .filter(Boolean);

    const standardDescriptionType = property.offer.standard_description?.types?.[0];
    const standard = standardDescriptionType ? variables?.offer_standard_description_type?.find((item) => item.value === standardDescriptionType)?.label : null;

    const height =
        property.height_range?.lower && property.height_range?.upper
            ? property.height_range.lower === property.height_range.upper
                ? `${property.height_range.lower} cm`
                : `${property.height_range.lower}-${property.height_range.upper} cm`
            : null;

    const facilities = (property.offer.facilities ?? [])
        .map((facility) => variables?.offer_facilities?.find((item) => item.value === facility)?.label)
        .filter(Boolean);

    const uniqueParkingPlaces = Array.from(new Set(parkingPlaces));

    return [
        {label: "Miejsca postojowe", value: uniqueParkingPlaces.length ? uniqueParkingPlaces.join(", ") : TINDER_MULTI_LEAD_EMPTY_VALUE},
        {
            label: "Komórki lokatorskie",
            value: property.offer.storage?.quantity ? `${property.offer.storage.quantity} dostępne` : TINDER_MULTI_LEAD_EMPTY_VALUE
        },
        {label: "Standard wykończenia", value: standard || TINDER_MULTI_LEAD_EMPTY_VALUE},
        {label: "Rodzaj ogrzewania", value: property.offer.standard_description?.heating || TINDER_MULTI_LEAD_EMPTY_VALUE},
        {label: "Wysokość pomieszczeń", value: height || TINDER_MULTI_LEAD_EMPTY_VALUE},
        {label: "Udogodnienia", value: facilities.length ? facilities.join(", ") : TINDER_MULTI_LEAD_EMPTY_VALUE}
    ];
}
